import React, { useState, useRef, useCallback, useEffect } from 'react';
import toast from 'react-hot-toast';
import VideoRecorder from './utils/VideoRecorder';
import { EVALUATION_ENDPOINTS, QUESTIONNAIRE_ENDPOINTS } from '../../constants/apiEndpoints';

const VideoAnswerQuestionWithSave = React.memo(function VideoAnswerQuestionWithSave({ 
  question, 
  value = '', 
  onChange, 
  disabled = false,
  challengeInstanceId,
  questionnaireInstanceId
}) {
  // AUTO-DETECT which context we're in
  const isQuestionnaire = !!questionnaireInstanceId;
  const instanceId = questionnaireInstanceId || challengeInstanceId;
  const endpoint = isQuestionnaire 
    ? QUESTIONNAIRE_ENDPOINTS.SAVE_VIDEO_ANSWER 
    : EVALUATION_ENDPOINTS.SAVE_VIDEO_ANSWER;
  const instanceIdKey = isQuestionnaire ? 'qi_id' : 'ci_id';
  
  const [isSaving, setIsSaving] = useState(false);
  const [currentBlobUrl, setCurrentBlobUrl] = useState(null);
  const [savedVideoUrl, setSavedVideoUrl] = useState(value || null);
  const [isRecorderActive, setIsRecorderActive] = useState(false);
  const processingRef = useRef(null);
  const onChangeRef = useRef(onChange);
  
  // Keep onChange ref up to date
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const handleVideoRecorded = useCallback(async (videoBlobUrl) => {
    if (!videoBlobUrl || !videoBlobUrl.startsWith('blob:')) {
      return;
    }

    // Prevent duplicate processing of the same blob URL
    if (processingRef.current === videoBlobUrl || isSaving) {
      console.log('Already processing this video, skipping duplicate request');
      return;
    }

    processingRef.current = videoBlobUrl;
    setIsSaving(true);
    setCurrentBlobUrl(videoBlobUrl);
    
    try {
      // Convert blob URL to actual blob
      const videoBlob = await fetch(videoBlobUrl).then(res => res.blob());
      
      // Create FormData for video upload
      const formData = new FormData();
      formData.append(instanceIdKey, instanceId);
      formData.append('question_id', question.id);
      formData.append('video', videoBlob, 'video-answer.webm');
      
      console.log(`Saving video to ${endpoint}:`, {
        [instanceIdKey]: instanceId,
        question_id: question.id,
        questionText: question.questionText
      });
      
      // Submit to server for saving
      const response = await fetch(endpoint, {
        method: 'POST',
        body: formData
      });
      
      if (!response.ok) {
        throw new Error(`Failed to save video: ${response.status}`);
      }
      
      const result = await response.json();
      console.log('Video save result:', result);
      
      // Extract the data object from array response
      const data = result[0];
      
      // Extract the file_id, file_key, and text from server response
      const fileId = data.file_id;
      const fileKey = data.file_key;
      const text = data.text;
      
      if (!fileId || !fileKey) {
        throw new Error('No file_id or file_key received from server');
      }
      
      // Create complete answer object with all video data
      const answerData = {
        file_id: fileId,
        file_key: fileKey,
        answer: text || '' // Transcribed answer text (empty string if not available)
      };
      
      console.log('Video answer data:', JSON.stringify(answerData, null, 2));
      
      // Keep the blob URL for video player (so user can watch their recording)
      setSavedVideoUrl(videoBlobUrl);
      // Pass the complete answer object to onChange (for responses state) using ref
      onChangeRef.current(answerData);
      
    } catch (err) {
      console.error('Error saving video:', err);
      
      // Show user-friendly error message using toast
      toast.error('Unable to save your video recording. Please try recording again.', {
        duration: 5000,
        position: 'top-center',
      });
      
      // Clear the blob URL on error so user can try again
      setCurrentBlobUrl(null);
      processingRef.current = null;
    } finally {
      setIsSaving(false);
    }
  }, [isSaving, question.id, instanceId, endpoint, instanceIdKey]);

  const handleRecordAgain = useCallback(() => {
    setCurrentBlobUrl(null);
    setSavedVideoUrl(null);
    setIsRecorderActive(false);
    processingRef.current = null;
    onChangeRef.current('');
  }, []);
  
  // Determine which URL to use for playback:
  // 1. If value has url (signed URL from server), use that
  // 2. Otherwise use savedVideoUrl (blob URL from current session)
  const playbackUrl = value?.url || savedVideoUrl;

  return (
    <div>
      {!playbackUrl ? (
        !isRecorderActive ? (
          // Show "Start Recording" button before activating camera
          <div className="w-full">
            <div className="rounded-lg border border-gray-300 bg-gray-50 p-8 flex flex-col items-center justify-center" style={{ minHeight: '360px' }}>
              <svg className="w-16 h-16 mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              <p className="text-gray-600 mb-4 text-center">Click below to enable your camera for recording your answer</p>
              <button
                type="button"
                onClick={() => setIsRecorderActive(true)}
                disabled={disabled}
                className={`px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-medium rounded-lg hover:from-blue-600 hover:to-blue-700 transition-colors duration-200 shadow-md flex items-center ${
                  disabled ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" strokeWidth="2" />
                  <circle cx="12" cy="12" r="4" fill="currentColor" />
                </svg>
                Enable Camera
              </button>
            </div>
          </div>
        ) : (
          // Show VideoRecorder after button click
          <VideoRecorder 
            value={currentBlobUrl || ''}
            onChange={handleVideoRecorded}
            disabled={disabled || isSaving}
            maxRecordingTime={question.max_duration || 120}
            isSaving={isSaving}
          />
        )
      ) : (
        <div className="w-full">
          <div className="relative w-full rounded-lg border border-[#33C6B6] bg-gray-50 p-4 flex flex-col justify-between">
            {/* Success message and video player */}
            <div className="flex-1 flex flex-col items-center justify-center">
              <p className="text-sm text-green-700 font-medium mb-3">✓ Video saved successfully</p>
              <video 
                src={playbackUrl}
                controls 
                className="w-full max-w-2xl rounded-lg"
                style={{ height: '360px' }}
                preload="metadata"
              />
            </div>
            
            {/* Record Again Button */}
            <div className="flex justify-center mt-4 mb-2">
              <button
                type="button"
                onClick={handleRecordAgain}
                disabled={disabled}
                className={`px-6 py-3 bg-gradient-to-r from-gray-500 to-gray-600 text-white font-medium rounded-lg hover:from-gray-600 hover:to-gray-700 transition-colors duration-200 shadow-md flex items-center ${
                  disabled ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Record Again
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default VideoAnswerQuestionWithSave;
